import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone } from "lucide-react";
import { EASE } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteForm() {
  const [form, setForm] = useState({
    nom: "",
    telephone: "",
    courriel: "",
    projet: "",
  });
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState({ token: "", question: "" });
  const [reponse, setReponse] = useState("");

  const chargerCaptcha = async () => {
    try {
      const r = await axios.get(`${API}/captcha`);
      setCaptcha({ token: r.data.token, question: r.data.question });
      setReponse("");
    } catch {
      toast.error("Impossible de charger la vérification. Rafraîchissez la page.");
    }
  };

  useEffect(() => {
    chargerCaptcha();
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/soumissions`, {
        nom: form.nom,
        telephone: form.telephone,
        projet: form.projet,
        ...(form.courriel ? { courriel: form.courriel } : {}),
        captcha_token: captcha.token,
        captcha_reponse: Number(reponse),
      });
      toast.success("Merci! Votre demande de soumission a bien été reçue. Nous vous contacterons sous peu.");
      setForm({ nom: "", telephone: "", courriel: "", projet: "" });
      chargerCaptcha();
    } catch (err) {
      if (err?.response?.status === 400) {
        toast.error("Réponse à la vérification incorrecte. Réessayez.");
      } else {
        toast.error("Une erreur est survenue. Appelez-nous au 438-985-1797.");
      }
      chargerCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="soumission"
      data-testid="quote-section"
      className="py-32 md:py-44"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-14 md:gap-20">
        <motion.div
          className="md:col-span-5"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.1, ease: EASE }}
        >
          <p className="uppercase tracking-[0.25em] text-xs text-[#D1BFAE]">
            Soumission gratuite
          </p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight mt-6 text-[#F9F9F9]">
            Parlons de votre <em className="text-[#D1BFAE]">projet</em>
          </h2>
          <p className="text-[#C4C9CE] text-base sm:text-lg leading-relaxed mt-6">
            Rénovation complète, du premier croquis à la dernière
            finition. Décrivez-nous votre projet — nous vous rappelons
            rapidement.
          </p>
          <p
            data-testid="quote-rbq"
            className="mt-10 text-xs uppercase tracking-[0.15em] text-[#C4C9CE]"
          >
            RBQ : Q:5881-6240-01
          </p>
          <a
            data-testid="quote-phone-link"
            href="tel:4389851797"
            className="mt-3 inline-flex items-center gap-3 font-display text-3xl text-[#F9F9F9] hover:text-[#D1BFAE] transition-colors duration-300"
          >
            <Phone size={24} strokeWidth={1.25} className="text-[#D1BFAE]" />
            438-985-1797
          </a>
        </motion.div>

        <motion.form
          data-testid="quote-form"
          onSubmit={submit}
          className="md:col-span-7 space-y-9"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.15 }}
        >
          <div className="grid sm:grid-cols-2 gap-9">
            <div>
              <label htmlFor="quote-nom" className="block uppercase tracking-[0.2em] text-xs text-[#C4C9CE] mb-2">
                Nom complet *
              </label>
              <input
                id="quote-nom"
                data-testid="quote-nom-input"
                required
                value={form.nom}
                onChange={update("nom")}
                placeholder="Votre nom"
                className="input-line"
              />
            </div>
            <div>
              <label htmlFor="quote-telephone" className="block uppercase tracking-[0.2em] text-xs text-[#C4C9CE] mb-2">
                Téléphone *
              </label>
              <input
                id="quote-telephone"
                data-testid="quote-telephone-input"
                required
                type="tel"
                value={form.telephone}
                onChange={update("telephone")}
                placeholder="000-000-0000"
                className="input-line"
              />
            </div>
          </div>
          <div>
            <label htmlFor="quote-courriel" className="block uppercase tracking-[0.2em] text-xs text-[#C4C9CE] mb-2">
              Courriel (optionnel)
            </label>
            <input
              id="quote-courriel"
              data-testid="quote-courriel-input"
              type="email"
              value={form.courriel}
              onChange={update("courriel")}
              placeholder="vous@exemple.com"
              className="input-line"
            />
          </div>
          <div>
            <label htmlFor="quote-projet" className="block uppercase tracking-[0.2em] text-xs text-[#C4C9CE] mb-2">
              Votre projet *
            </label>
            <textarea
              id="quote-projet"
              data-testid="quote-projet-input"
              required
              rows={4}
              value={form.projet}
              onChange={update("projet")}
              placeholder="Décrivez votre projet de rénovation…"
              className="input-line resize-none"
            />
          </div>
          <div>
            <label htmlFor="quote-captcha" className="block uppercase tracking-[0.2em] text-xs text-[#C4C9CE] mb-2">
              Vérification anti-robot : {captcha.question}
            </label>
            <input
              id="quote-captcha"
              data-testid="quote-captcha-input"
              required
              inputMode="numeric"
              value={reponse}
              onChange={(e) => setReponse(e.target.value)}
              placeholder="Votre réponse"
              className="input-line"
            />
          </div>
          <button
            data-testid="quote-submit-button"
            type="submit"
            disabled={loading}
            className="relative overflow-hidden bg-[#D1BFAE] text-[#1a1a1a] px-8 py-4 text-xs uppercase tracking-[0.15em] group disabled:opacity-60"
          >
            <span className="absolute inset-0 bg-[#F9F9F9] translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
            <span className="relative">
              {loading ? "Envoi en cours…" : "Envoyer ma demande"}
            </span>
          </button>
        </motion.form>
      </div>
    </section>
  );
}
